import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { companyService, type CreateCompanyDto, type UpdateCompanyDto } from "@/services/company/company.service";
import { UtilsService } from "@/services/utils/utils.service";
import { useCompanyStore } from "@/store/use-company-store";
import { useAuthStore } from "@/store/use-auth-store";

const utils = new UtilsService();

export const useCompany = () => {
  const { currentCompanyId } = useCompanyStore();
  const { isAuthenticated } = useAuthStore();
  
  return useQuery({
    queryKey: ["company", currentCompanyId],
    queryFn: () => companyService.getCompany(),
    enabled: isAuthenticated && !!currentCompanyId,
    retry: false,
  });
};

export const useCreateCompany = () => {
  const queryClient = useQueryClient();
  const { refreshMemberships, setCurrentCompany } = useCompanyStore();

  return useMutation({
    mutationFn: (data: CreateCompanyDto) => companyService.createCompany(data),
    onSuccess: async (company) => {
      queryClient.invalidateQueries({ queryKey: ["company"] });
      queryClient.invalidateQueries({ queryKey: ["onboardingStatus"] });
      queryClient.invalidateQueries({ queryKey: ["userMemberships"] });
      
      // Refresh memberships and set new company as active
      await refreshMemberships();
      if (company?.id) {
        setCurrentCompany(company.id);
      }
    },
    onError: (error: unknown) => {
      // Error handling is done in the component
      throw error;
    },
  });
};

export const useUpdateCompany = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentCompanyId } = useCompanyStore();

  return useMutation({
    mutationFn: ({ companyId, data }: { companyId: string; data: UpdateCompanyDto }) =>
      companyService.updateCompany(companyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company", currentCompanyId] });
      toast.success(t("company.details.updated"));
    },
    onError: (error: unknown) => {
      const errorMessage = utils.extractErrorMessage(
        error,
        t("onboarding.companyUpdateFailed")
      );
      toast.error(errorMessage);
    },
  });
};

export const useCompanyMembers = () => {
  const { currentCompanyId } = useCompanyStore();
  const { isAuthenticated } = useAuthStore();
  
  return useQuery({
    queryKey: ["companyMembers", currentCompanyId],
    queryFn: () => companyService.getMembers(),
    enabled: isAuthenticated && !!currentCompanyId,
    retry: false,
  });
};

export const useDeleteCompany = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { refreshMemberships, clearCompany } = useCompanyStore();

  return useMutation({
    mutationFn: ({ companyId, suppressToast }: { companyId: string; suppressToast?: boolean } = { companyId: '', suppressToast: false }) => {
      return companyService.deleteCompany(companyId).then(result => ({ result, suppressToast: suppressToast ?? false }));
    },
    onSuccess: async (data: { result: any; suppressToast: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ["company"] });
      queryClient.invalidateQueries({ queryKey: ["userMemberships"] });
      queryClient.invalidateQueries({ queryKey: ["onboardingStatus"] });
      
      await refreshMemberships();
      // Clear company if it was the deleted one
      clearCompany();
      
      if (!data.suppressToast) {
        toast.success(t('company.deletedSuccess') || 'Company deleted successfully');
      }
    },
    onError: (error: unknown) => {
      const errorMessage = utils.extractErrorMessage(
        error,
        t('company.deleteFailed') || 'Failed to delete company'
      );
      toast.error(errorMessage);
    },
  });
};

